import { pdfTenantsWatermarkConfig } from './tenant.pdf-watermark';
import type { TenantConfig, TenantName, TenantPdfConfig } from './types';

const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1);

const client: TenantName = import.meta.env?.VITE_CLIENT ?? process?.env?.CLIENT;
if (!client) {
  throw new Error('Missing env var: CLIENT');
}

const appName: string = import.meta.env?.VITE_APP_NAME ?? process?.env?.APP_NAME;
if (!appName) {
  throw new Error('Missing env var: APP_NAME');
}

export const pdfTenantsConfig = (client: TenantName): TenantPdfConfig => {
  const defaultPdfConfig: TenantPdfConfig = {
    format: 'A4',
    documents: {
      templates: ['template-document'],
      submissions: ['submission-document'],
      endorsements: ['endorsement-summary'],
      brandings: ['branding-document'],
    },
  };

  switch (client) {
    case 'ct-ppl':
      return defaultPdfConfig;
    default:
      return {
        ...defaultPdfConfig,
        watermark: pdfTenantsWatermarkConfig({ client }),
      };
  }
};

const cognitoConfig = (forClient: TenantName): TenantConfig['cognito'] => ({
  linkedLogo: '/assets/Artificial_Logo_Email_New.png',
  emailSender: 'noreply@mail.contract-builder.artificialos.com',
  appName,
  appDescription: `${appName} is a contract management platform. If you believe you've received this email in error, please contact your administrator.`,
  client: capitalize(forClient),
  footerAddress: '',
});

const artificialBrandingConfig = {
  logo: '/assets/Artificial_Logo_Login_New.svg',
  headerLogo: '/assets/Artificial_Logo_White_New.svg',
  headerLogoClasses: 'h-[17px]',
  headerLogoHeight: '17',
  headerLogoWidth: '180',
};

const artificialConfig = (forClient: TenantName): TenantConfig => {
  return {
    name: 'Contract builder',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('artificial'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isRevisionsEnabled: true,
    isSSOEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctAonConfig = (forClient: TenantName): TenantConfig => {
  return {
    name: 'Contract builder',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('artificial'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isRevisionsEnabled: true,
    isSSOEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctLockton = (forClient: TenantName): TenantConfig => {
  return {
    name: 'Lockton',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('ct-lockton'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    isTemplateReviewWorkflowEnabled: false,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctBms = (forClient: TenantName): TenantConfig => {
  return {
    name: 'BMS',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('ct-bms'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    isTemplateReviewWorkflowEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctAjg = (forClient: TenantName): TenantConfig => {
  return {
    name: 'AJG',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('ct-ajg'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctHowden = (forClient: TenantName): TenantConfig => {
  return {
    name: 'Howden',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('ct-howden'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctMgcill = (forClient: TenantName): TenantConfig => {
  return {
    name: 'Mcgill',
    ...artificialBrandingConfig,
    pdf: pdfTenantsConfig('ct-mcgill'),
    isAttachmentsEnabled: true,
    isEndorsementEnabled: true,
    isSSOEnabled: true,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

const ctPpl = (forClient: TenantName): TenantConfig => {
  return {
    name: 'PPL',
    logo: '/assets/Artificial_Logo_Black.svg',
    headerLogo: '/assets/Artificial_Logo_White.png',
    headerLogoClasses: 'h-12',
    headerLogoHeight: '48',
    headerLogoWidth: '130',
    pdf: pdfTenantsConfig('ct-ppl'),
    isAttachmentsEnabled: true,
    isBrandingEnabled: true,
    isEndorsementEnabled: false,
    isTempContractEnabled: false,
    isSwapBaseTemplateEnabled: false,
    isSaveCloseEnabled: false,
    cognito: cognitoConfig(forClient),
    teams: [],
  };
};

// Exhaustive over TenantName, so a new client is a compile error rather than a silent
// fall-through to the Artificial config.
const TENANT_CONFIG_BUILDERS: Record<TenantName, (forClient: TenantName) => TenantConfig> = {
  artificial: artificialConfig,
  'smart-placement': artificialConfig,
  'ct-aon': ctAonConfig,
  'ct-lockton': ctLockton,
  'ct-bms': ctBms,
  'ct-ajg': ctAjg,
  'ct-howden': ctHowden,
  'ct-mcgill': ctMgcill,
  'ct-ppl': ctPpl,
};

export const isTenantName = (value: string): value is TenantName => Object.hasOwn(TENANT_CONFIG_BUILDERS, value);

// Throws on an unknown client rather than serving the Artificial config under another name.
export const getTenantConfig = (forClient: TenantName = client): TenantConfig => {
  if (!isTenantName(forClient)) {
    throw new Error(`Unknown client: ${forClient}`);
  }
  return TENANT_CONFIG_BUILDERS[forClient](forClient);
};

export const tenantConfig: TenantConfig = getTenantConfig();
